import type { JSX } from 'preact';
import { useCallback } from 'preact/hooks';
import { Badge } from 'components/atoms/Badge.tsx';
import { Checkbox } from 'components/atoms/form/Checkbox.tsx';
import { multiselectOffers } from 'components/bazar/signals';
import { OfferOptions } from 'components/offer/OfferOptions';
import { localize } from 'i18n/localize.ts';
import { generateOfferDetailLink } from 'utils/url.ts';
import { cn } from 'utils/cn';
import Link from 'components/atoms/Link';
import { isCWLEnabled } from 'lib/cwl/utils';
import { useSignal } from '@preact/signals';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import { localFiltersToAnalyticsFilters } from 'lib/dot/utils.ts';
import { filters as globalFilters } from 'signals/filters';
import { useSignals } from 'context/SignalsContext.tsx';
import { favoriteOffers } from 'signals/favoriteOffers';
import { OfferRow } from 'components/offer/rows/OfferRow';
import { isInactive } from 'components/offer/utils/status';
import { type OfferData } from 'service/offer/model';
import { MessengerButtonContainer } from 'components/offer/MessengerButtonContainer';

const $t = localize();

export type Props = {
	analyticsPosition?: string;
	forceTopped?: boolean;
	lazyImage?: boolean;
	multiselectInputProps?: Omit<JSX.IntrinsicElements['input'], 'onClick'> & {
		onClick: (event: JSX.TargetedMouseEvent<HTMLInputElement>, offer?: OfferData) => void;
	};
	noLink?: boolean;
	offer: OfferData;
	showEditActions?: boolean;
	showFavoriteButton?: boolean;
	showInactive?: boolean;
	showMultiselect?: boolean;
	showReserved?: boolean;
	showToppedRibbon?: boolean;
	showVideo?: boolean;
	voucherCount?: number;
	onOfferDelete?: (event: Event, offer: Partial<OfferData>) => void;
	onOfferToggleVisibility?: (event: Event, offer: Partial<OfferData>) => void;
	onOfferReserve?: (event: Event, offer: Partial<OfferData>) => void;
};

/**
 * For easier styling this component relies on tailwind and also on its sibling .less file for nested CSS selectors.
 */
export function OfferRowWithOptions({
	analyticsPosition,
	lazyImage,
	multiselectInputProps,
	noLink,
	offer,
	showInactive = true,
	showMultiselect = false,
	showReserved = true,
	voucherCount,
	onOfferDelete,
	onOfferToggleVisibility,
	onOfferReserve,
}: Props) {
	const { filters = globalFilters } = useSignals();
	const isDropdownOpen = useSignal(false);

	const onClickCallback = useCallback(
		(event: JSX.TargetedMouseEvent<HTMLInputElement>) => {
			if (multiselectInputProps?.onClick) multiselectInputProps?.onClick(event, offer);
		},
		[multiselectInputProps, offer],
	);
	const isReserved = showReserved && offer.isReserved;
	const isOfferInactive =
		showInactive &&
		isInactive(offer.status) &&
		!multiselectOffers.value.enabled &&
		offer.approvalAdmin !== 'rejected';
	const offerRowProps = {
		className: isReserved || isOfferInactive ? 'mdp:mr-80' : 'mdp:mr-48',
		offer: offer,
		lazyImage: lazyImage,
	};

	return (
		<div class="min-h-20 w-full flex">
			{showMultiselect && multiselectOffers.value.enabled && (
				<div class="py-3 pr-1 pl-0 my-3 flex-shrink-0">
					<Checkbox
						color="red"
						className="p-[7px]"
						title={$t.offer.multiselect}
						inputProps={{
							...(multiselectInputProps || {}),
							onClick: onClickCallback,
							checked: Boolean(offer.id) && multiselectOffers.value.selectedOffers.has(offer.id),
						}}
					/>
				</div>
			)}
			<div class="relative @container group/card min-h-20 w-full">
				{noLink ? (
					<OfferRow {...offerRowProps} />
				) : (
					<Link
						cwlActive={isCWLEnabled()}
						classNameOverride="block mdp:group-hover/card:bg-gray-background w-full h-full overflow-hidden rounded-2xl"
						href={offer.seoName && generateOfferDetailLink(offer)}
						onClick={() => {
							const _filters = filters.peek();
							const searchType = DOTHelper.getStore().searchType;

							DOTHelper.setOfferDetailMeta({
								...(_filters && { filters: localFiltersToAnalyticsFilters(_filters) }),
								...(searchType && { searchType }),
								saved: DOTHelper.sanitizeBoolean(favoriteOffers.value?.[offer.id]),
								advertPosition: analyticsPosition,
							});
						}}
					>
						<OfferRow {...offerRowProps} />
					</Link>
				)}
				<div
					class={cn(
						'absolute inline-flex gap-2 mdp:gap-5 items-end mdp:items-center top-0 right-0 mdp:right-8 h-full',
						isDropdownOpen.value ? undefined : 'pointer-events-none [&>*]:pointer-events-auto',
					)}
				>
					<span class="hidden mdp:inline text-dark-blue-50">
						{offer.statistics?.detailView ?? 0} {$t.offer.displays}
					</span>
					{isOfferInactive && (
						<Badge
							className="hidden mdp:inline-flex h-8 leading-4 pointer-events-none"
							type="dark-blue-transparent"
						>
							{$t.offer.stateInactive}
						</Badge>
					)}
					{!isInactive && isReserved && (
						<Badge type="white" className="hidden mdp:inline-flex h-8 leading-4 pointer-events-none">
							{$t.offer.stateReserved}
						</Badge>
					)}
					<MessengerButtonContainer
						className="p-[9px] mdp:p-0.5 bg-white mdp:bg-transparent rounded-full border border-solid border-gray mdp:border-0 hover:border-dark-blue"
						offer={offer}
					/>
					<OfferOptions
						openerClassName="size-6 hidden mdp:flex"
						voucherCount={voucherCount}
						offer={offer}
						onOfferDelete={onOfferDelete}
						onOfferToggleVisibility={onOfferToggleVisibility}
						onOfferReserve={onOfferReserve}
						onDropdownOpen={() => (isDropdownOpen.value = true)}
						onDropdownClose={() => (isDropdownOpen.value = false)}
						forceTransparentOpener
					/>
				</div>
			</div>
		</div>
	);
}
